import axios from "axios";

// Helper function to get API URL - no longer uses env vars
// Configuration is now fetched from backend via /api/config/frontend
export const getApiUrl = () => {
  // Check if we're in development or production
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'https://www.uttaapp.com';
  }
  
  // Production - use relative URL to ensure it always uses the same domain
  // This is the most robust way to avoid domain mismatch errors
  return ''; 
};

// Define main URL - no longer uses environment variables
const mainUrl = getApiUrl();
const apiBasePath = "/api";
const apiBaseUrl = mainUrl + apiBasePath;

// Debug: Log API 
console.log('🔧 API Configuration:', {
  mainUrl,
  apiBasePath,
  apiBaseUrl,
  note: 'API keys and other config now loaded from backend /api/config/frontend'
});

// Create Api object with all endpoints
const Api = {
  MainUrl: mainUrl,
  BaseApiUrl: apiBaseUrl,
  
  // Authentication endpoints
  Auth: {
    register: apiBaseUrl + "/register",
    login: apiBaseUrl + "/login",
    validateToken: apiBaseUrl + "/validate-token",
    logout: apiBaseUrl + "/logout",
    sendOTP: apiBaseUrl + "/send-otp",
    sendLoginOTP: apiBaseUrl + "/send-login-otp",
    verifyLoginOTP: apiBaseUrl + "/verify-login-otp",
    verifyOTP: apiBaseUrl + "/verify-otp",
    resendOTP: apiBaseUrl + "/resend-otp",
    requestLoginOTP: apiBaseUrl + "/request-login-otp",
    googleLogin: apiBaseUrl + "/google-login",
    facebookLogin: apiBaseUrl + "/facebook-login",
    completeSocialLogin: apiBaseUrl + "/complete-social-login",
    userToken: (userId) => apiBaseUrl + "/user-token" + (userId ? "/" + encodeURIComponent(userId) : ""),
  },

  // User endpoints
  User: {
    profile: apiBaseUrl + "/users/profile",
    updateProfile: apiBaseUrl + "/user/profile",
    updateProfilePicture: apiBaseUrl + "/user/profile-picture",
    becomeDriver: apiBaseUrl + "/users/become-driver",
    rides: apiBaseUrl + "/users/rides",
    updateDriver: apiBaseUrl + "/user/update-driver",
    status: apiBaseUrl + "/user/status",
  },

  // Driver endpoints
  Driver: {
    profile: (driverId) => apiBaseUrl + "/driver/profile/" + encodeURIComponent(driverId),
    updateProfile: apiBaseUrl + "/driver/profile",
    updateProfilePicture: apiBaseUrl + "/driver/profile-picture",
    register: apiBaseUrl + "/driver/register",
    completeOnboarding: apiBaseUrl + "/driver/complete-onboarding",
    updateLocation: apiBaseUrl + "/driver/update-location",
    toggleStatus: apiBaseUrl + "/driver/toggle-status",
    availability: apiBaseUrl + "/drivers/availability",
    location: apiBaseUrl + "/drivers/location",
    currentLocation: apiBaseUrl + "/drivers/current-location",
    earnings: apiBaseUrl + "/driver/earnings",
    rating: apiBaseUrl + "/driver/rating",
    rides: apiBaseUrl + "/drivers/rides",
    vehicle: apiBaseUrl + "/drivers/vehicle",
    status: apiBaseUrl + "/drivers/status",
    active: apiBaseUrl + "/drivers/active",
    online: apiBaseUrl + "/drivers/online",
    nearby: apiBaseUrl + "/drivers/nearby",
    nearbyByVehicle: apiBaseUrl + "/drivers/nearby-by-vehicle",
    activeStatus: apiBaseUrl + "/drivers/active-status",
    testUpdateVehicle: apiBaseUrl + "/drivers/test-update-vehicle",
    debugVehicle: apiBaseUrl + "/drivers/debug-vehicle",
    details: apiBaseUrl + "/driver/details",
    bankDetails: apiBaseUrl + "/driver/bank-details",
    ifscLookup: (ifsc) => apiBaseUrl + "/driver/bank-details/ifsc/" + encodeURIComponent(ifsc),
    reserveBooking: apiBaseUrl + "/drivers/reserve-booking",
    sessionStatus: apiBaseUrl + "/driver/session-status",
  },

  // Aadhaar verification endpoints
  Aadhaar: {
    sendOtp: apiBaseUrl + "/aadhaar/sendotp",
    verifyOtp: apiBaseUrl + "/aadhaar/verifyotp",
    saveAadhaar: apiBaseUrl + "/aadhaar/saveAadhar",
    getDetails: apiBaseUrl + "/aadhaar/details",
  },

  // Place endpoints
  Place: {
    savePlace: apiBaseUrl + "/places/save-place",
    getPlace: (placeId) => apiBaseUrl + "/places/place/" + encodeURIComponent(placeId),
    getAllPlaces: apiBaseUrl + "/places/places",
    searchPlaces: (query) => apiBaseUrl + "/places/search?q=" + encodeURIComponent(query),
    googleAutocomplete: (query, country) => apiBaseUrl + "/places/google-autocomplete?query=" + encodeURIComponent(query) + (country ? "&country=" + encodeURIComponent(country) : ""),
    googlePlaceDetails: (placeId) => apiBaseUrl + "/places/google-place-details/" + encodeURIComponent(placeId),
  },

  // Ride booking endpoints
  Ride: {
    book: apiBaseUrl + "/book-ride",
    reserve: apiBaseUrl + "/reserve-ride",
    availableDrivers: (pincode) => apiBaseUrl + "/rides/available-drivers/" + encodeURIComponent(pincode),
    nearbyDrivers: (pincode, vehicleType, bookingType) => {
      let url = apiBaseUrl + "/rides/nearby-drivers/" + encodeURIComponent(pincode) + "/" + encodeURIComponent(vehicleType);
      if (bookingType) {
        url += "?bookingType=" + encodeURIComponent(bookingType);
      }
      return url;
    },
    notifyAllDrivers: apiBaseUrl + "/rides/notify-all-drivers",
    userRides: (username) => apiBaseUrl + "/user-rides/" + encodeURIComponent(username),
    details: (rideId) => apiBaseUrl + "/rides/" + encodeURIComponent(rideId),
    update: (rideId) => apiBaseUrl + "/rides/" + encodeURIComponent(rideId),
    cancel: (rideId) => apiBaseUrl + "/rides/" + encodeURIComponent(rideId) + "/cancel",
    complete: (rideId) => apiBaseUrl + "/rides/" + encodeURIComponent(rideId) + "/complete",
  },

  // Booking endpoints
  Booking: {
    create: apiBaseUrl + "/bookings/create",
    userBookings: (username) => apiBaseUrl + "/bookings/user/" + encodeURIComponent(username),
    myBookings: apiBaseUrl + "/bookings/my-bookings", // Secure endpoint for authenticated user
    myDriverBookings: apiBaseUrl + "/bookings/my-driver-bookings", // Secure endpoint for authenticated driver
    driverBookings: (driverId) => apiBaseUrl + "/bookings/driver/" + encodeURIComponent(driverId),
    updateStatus: (bookingId) => apiBaseUrl + "/bookings/" + encodeURIComponent(bookingId) + "/status",
    proposeAmount: (bookingId) => apiBaseUrl + "/bookings/" + encodeURIComponent(bookingId) + "/propose-amount",
    acceptAmount: (bookingId) => apiBaseUrl + "/bookings/" + encodeURIComponent(bookingId) + "/accept-amount",
    cancelNegotiation: (bookingId) => apiBaseUrl + "/bookings/" + encodeURIComponent(bookingId) + "/cancel-negotiation",
    verifyOTP: (bookingId) => apiBaseUrl + "/bookings/" + encodeURIComponent(bookingId) + "/verify-otp",
    generateOTPOnArrival: (bookingId) => apiBaseUrl + "/bookings/" + encodeURIComponent(bookingId) + "/generate-otp-on-arrival",
    completeRide: (bookingId) => apiBaseUrl + "/bookings/" + encodeURIComponent(bookingId) + "/complete-ride",
    getBooking: (bookingId) => apiBaseUrl + "/bookings/" + encodeURIComponent(bookingId),
    submitRating: (bookingId) => apiBaseUrl + "/bookings/" + encodeURIComponent(bookingId) + "/rating",
    updateReserveDates: (bookingId) => apiBaseUrl + "/bookings/" + encodeURIComponent(bookingId) + "/update-reserve-dates",
    startTrip: (bookingId) => apiBaseUrl + "/bookings/" + encodeURIComponent(bookingId) + "/start-trip",
    cancel: (bookingId) => apiBaseUrl + "/bookings/" + encodeURIComponent(bookingId) + "/status",
    sendFindAnotherDriverNotification: (bookingId) => apiBaseUrl + "/bookings/" + encodeURIComponent(bookingId) + "/send-find-another-driver-notification",
    sendExpiredBookingNotification: (bookingId) => apiBaseUrl + "/bookings/" + encodeURIComponent(bookingId) + "/send-expired-booking-notification",
  },

  // Payment endpoints (New Razorpay Payment System)
  Payment: {
    createRazorpayOrder: apiBaseUrl + "/payments/create-razorpay-order",
    updatePaymentStatus: apiBaseUrl + "/payments/update-payment-status",
    verifyPayment: apiBaseUrl + "/payments/verify-razorpay-payment",
    confirmPaymentReceived: apiBaseUrl + "/payments/confirm-payment-received",
    razorpayStatus: apiBaseUrl + "/payments/razorpay-status",
  },

  // Bank details endpoints
  BankDetails: {
    save: apiBaseUrl + "/driver/bank-details",
    get: (driverId) => apiBaseUrl + "/driver/bank-details/" + encodeURIComponent(driverId),
    update: (driverId) => apiBaseUrl + "/driver/bank-details/" + encodeURIComponent(driverId),
    verify: (driverId) => apiBaseUrl + "/driver/bank-details/" + encodeURIComponent(driverId) + "/verify",
  },

  // File upload endpoints
  Upload: {
    profilePicture: apiBaseUrl + "/upload/profile-picture",
    document: apiBaseUrl + "/upload/document",
    vehiclePhoto: apiBaseUrl + "/upload/vehicle-photo",
    pickupPhoto: apiBaseUrl + "/upload/pickup-photo",
  },

  // Utility endpoints
  Utility: {
    pincodeSearch: (query) => apiBaseUrl + "/pincode-search?query=" + encodeURIComponent(query),
    pincodeSearchPublic: (query) => apiBaseUrl + "/pincode-search-public?query=" + encodeURIComponent(query),
    health: apiBaseUrl + "/health",
    testSMTP: apiBaseUrl + "/test-smtp",
    sendAadhaarOTP: apiBaseUrl + "/send-aadhaar-otp",
    chatbot: apiBaseUrl + "/chatbot",
  },

  // Legacy Payment endpoints (if needed for other features)
  LegacyPayment: {
    createPayment: apiBaseUrl + "/payment/create",
    verifyPayment: apiBaseUrl + "/payment/verify",
    refund: apiBaseUrl + "/payment/refund",
    history: apiBaseUrl + "/payment/history",
  },

  // Notification endpoints
  Notification: {
    getAll: apiBaseUrl + "/notifications",
    unreadCount: apiBaseUrl + "/notifications/unread-count",
    markRead: (notificationId) => apiBaseUrl + "/notifications/" + encodeURIComponent(notificationId) + "/read",
    markReadByBookingId: (bookingId) => apiBaseUrl + "/notifications/read-by-booking/" + encodeURIComponent(bookingId),
    markAllRead: apiBaseUrl + "/notifications/read-all",
    clearAll: apiBaseUrl + "/notifications/clear-all",
    testNotification: (driverId) => apiBaseUrl + "/notifications/test-notification/" + encodeURIComponent(driverId),
  },

  // Rating endpoints
  Rating: {
    submitDriverRating: apiBaseUrl + "/ratings/driver-rating",
    submitUserRating: apiBaseUrl + "/ratings/user-rating",
    getDriverRatings: (driverId) => apiBaseUrl + "/ratings/driver-ratings/" + encodeURIComponent(driverId),
    getUserRatings: (userId) => apiBaseUrl + "/ratings/user-ratings/" + encodeURIComponent(userId),
  },

  // Report endpoints
  Report: {
    reportUser: apiBaseUrl + "/reports/report",
    myReports: apiBaseUrl + "/reports/my-reports",
  },

  // Admin endpoints
  Admin: {
    dashboard: apiBaseUrl + "/admin/dashboard",
    users: apiBaseUrl + "/admin/users",
    drivers: apiBaseUrl + "/admin/drivers",
    rides: apiBaseUrl + "/admin/rides",
    analytics: apiBaseUrl + "/admin/analytics",
    settings: apiBaseUrl + "/admin/settings",
  },

  // Policy endpoints (Public - no auth required)
  Policy: {
    privacyPolicy: apiBaseUrl + "/privacy-policy",
    termsConditions: apiBaseUrl + "/terms-conditions",
  },

  // Shared Ride endpoints
  SharedRide: {
    share: apiBaseUrl + "/shared-ride/share",
    getDetails: (token) => apiBaseUrl + "/shared-ride/" + encodeURIComponent(token),
    getLocation: (token) => apiBaseUrl + "/shared-ride/" + encodeURIComponent(token) + "/location",
  },

  // User Location endpoints
  UserLocation: {
    updateLocation: apiBaseUrl + "/user-location/update-location",
  }
};

// Helper function to create axios instance with default config
const createApiInstance = (baseURL = apiBaseUrl) => {
  const instance = axios.create({
    baseURL,
    timeout: 30000, // 30 seconds timeout
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add auth token to requests
  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem('uttaapp_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Handle response errors - NO automatic logout
  // User will only logout when they manually click logout button
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      // NEVER automatically logout on 401 errors
      // Only manual logout button should logout user
      if (error.response?.status === 401) {
        console.log('⚠️ 401 Unauthorized detected, but NOT logging out automatically');
        console.log('   User will remain logged in. Only manual logout will clear session.');
        // Just log the error, don't clear storage or redirect
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

// Helper function to add auth token to requests
const addAuthToken = (config, token) => {
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

// Helper function to handle API responses
const handleApiResponse = (response) => {
  return response.data;
};

// Helper function to handle API errors
const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    throw new Error(error.response.data.message || 'API request failed');
  } else if (error.request) {
    // Request was made but no response received
    throw new Error('Network error - please check your connection');
  } else {
    // Something else happened
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

// Export the Api object and helper functions
export default Api;
export { createApiInstance, addAuthToken, handleApiResponse, handleApiError };
